import React, { useCallback, useState } from 'react';
import { Modal } from 'reactstrap';
import type { Music24WishTopic, WishSurface } from 'fym-shared/src/analytics/wishTopics';
import { useSendWishMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { TypedMemo } from '../../common/types';
import { WishBoxForm } from './WishBoxForm';
import { WishBoxSent } from './WishBoxSent';
import styles from './WishBoxModal.module.scss';

interface WishBoxModalProps {
  from: WishSurface;
  onClose: () => void;
}

/**
 * The wish box (design wish-box a1-a4, a8): one topic and an optional note, in a modal over the
 * current page. A failed send keeps the topic and the note. The note goes in no event.
 */
const WishBoxModalComponent: React.FC<WishBoxModalProps> = ({ from, onClose }) => {
  const [topic, setTopic] = useState<Music24WishTopic | null>(null);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'failed' | 'sent'>('idle');
  const [sendWish, { isLoading }] = useSendWishMutation();

  const submit = useCallback(() => {
    if (topic === null) {
      return;
    }
    sendWish({ topic, surface: from, note: note.trim() })
      .then((result) => setStatus(isSuccessfulResult(result) ? 'sent' : 'failed'))
      .catch(() => setStatus('failed'));
  }, [from, note, sendWish, topic]);

  return (
    <Modal
      isOpen
      toggle={onClose}
      centered
      className={styles.modal}
      contentClassName={styles.dialog}
      labelledBy="wish-box-title"
    >
      {status === 'sent' ? (
        <WishBoxSent onDone={onClose} />
      ) : (
        <WishBoxForm
          topic={topic}
          note={note}
          isSending={isLoading}
          failed={status === 'failed'}
          onTopic={setTopic}
          onNote={setNote}
          onSend={submit}
          onClose={onClose}
        />
      )}
    </Modal>
  );
};

export const WishBoxModal = TypedMemo(WishBoxModalComponent);
